import { useState } from 'react';
import { Braces, SquarePen, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardHeader } from '../_primitives';
import { TYPE_LABELS } from '../schemaTypeLabels';

/**
 * Per-type value-presence row (T4 design ruling — Carl/Camille). One fixed
 * row per schema type, always in this order: Article, BreadcrumbList, HowTo,
 * FAQPage, Product. The card renders VALUE PRESENCE — an actual mini-fact
 * ("3 steps", "12 questions", a description snippet) or an honest empty /
 * unavailable state — never a count of types.
 */
export interface SchemaTypePresence {
  type: 'Article' | 'BreadcrumbList' | 'HowTo' | 'FAQPage' | 'Product';
  /**
   * Ship-semantic input: server-mandatory types (Article + BreadcrumbList)
   * that always ship. Shipping behavior must never be signified by a padlock
   * (one icon may not carry both editability and shipping semantics); the
   * "Ships automatically" group header carries it on this card. Kept typed
   * for the mount contract.
   */
  locked?: boolean;
  /**
   * 'insufficient' is the won't-ship state for gated automatic types:
   * publish ships FAQPage only at ≥2 Q&A pairs, so a single question must
   * never read as a shipping value — the row stays inert and informational.
   */
  state: 'value' | 'empty' | 'unavailable' | 'insufficient';
  /** Human-readable value presence, e.g. "3 steps", "no value yet", "—". */
  detail: string;
}

/**
 * The schema type a card row focuses when it opens the drawer. Stored by the
 * mount for the drawer's scoped open.
 */
export type SchemaFocusType = SchemaTypePresence['type'];

export interface StructuredDataCardProps {
  /**
   * Opens the structured-data drawer (right-side sheet). A non-inert row tap
   * passes its schema type as the focus; "Edit details" calls it with NO
   * focus.
   */
  onInspect: (focus?: SchemaFocusType) => void;
  /** Whether the shadow postgen_jsonld enrichment blob is present on the row. */
  enrichmentPresent?: boolean;
  /**
   * Fixed 5-row per-type value presence (T4 design ruling). Null/absent
   * before the drawer has been opened (no schema-preview cached yet) — the
   * card falls back to the neutral inspect CTA rather than a fabricated row
   * set.
   */
  typePresence?: SchemaTypePresence[] | null;
  /**
   * True while the family-E schema-authoring run is queued/running (or the
   * run request is immediately in flight). The TWO actionable rows render
   * labeled async skeletons (the wait must be named); automatic rows are
   * untouched.
   */
  authoring?: boolean;
}

/** Actionable (user-text) types in imperative-slot priority order. */
const ACTIONABLE_PRIORITY = ['Article', 'HowTo'] as const;
type ActionableType = (typeof ACTIONABLE_PRIORITY)[number];

/** Imperative copy for the serialized dashed slot / quiet text action. */
const SLOT_IMPERATIVES: Record<ActionableType, string> = {
  Article: 'Add an article summary',
  HowTo: 'Add how-to steps',
};

/** Labels for the authoring skeleton bars on the two actionable rows. */
const SKELETON_LABELS: Record<ActionableType, string> = {
  Article: 'Writing article summary…',
  HowTo: 'Writing how-to steps…',
};

const FOCUS_RING = 'focus:outline-none focus-visible:ring-1 focus-visible:ring-teal';

/**
 * Family E — slim rail card. The full JSON-LD graph (composed, per-node, with
 * per-field source tags) lives in the StructuredDataDrawer. This card is the
 * at-a-glance summary: a fixed row per schema type showing whether it has a
 * real authored/derived value yet, and one button to open the drawer.
 *
 * The card mirrors PER-TYPE VALUE PRESENCE, never an aggregate count.
 * `typePresence` is sourced from the backend schema-preview endpoint
 * (Article/HowTo authored fields + the composed FAQPage jsonld) — the
 * frontend never re-derives schema gating.
 *
 * Constraints (not visible from the code alone):
 *  - Group headers must be SYMMETRIC: "Ships automatically" (Breadcrumb
 *    trail, FAQ) and "Needs your text" (Article summary, How-to steps,
 *    Product details) — naming only one cluster re-creates the old
 *    half-grouping confusion.
 *  - The VALUE is the headline (13px font-medium; line-clamp-2 on Article
 *    summary; one-line truncate ONLY on the derived Breadcrumb trail); the
 *    label hugs ITS value beneath (11px regular muted — no bold+tracking
 *    microcopy).
 *  - Automatic rows stay inert/flat; their values render in the muted
 *    derived style with a quiet trailing "Automatic" microtag (read-only
 *    needs a POSITIVE cue — absence of a control is not perceivable).
 *  - At most ONE dashed imperative slot at a time; deferred empty rows stay
 *    visible as quiet text actions. Slot promotion after a fill gets a
 *    one-time, non-color, motion-safe emphasis (CSS animation only).
 */
export function StructuredDataCard({
  onInspect,
  enrichmentPresent,
  typePresence,
  authoring = false,
}: StructuredDataCardProps) {
  const rows = typePresence ?? [];
  const hasRows = rows.length > 0;
  const byType = new Map(rows.map((row) => [row.type, row]));

  // Serialized imperative slot: at most ONE dashed slot at a time — the
  // highest-priority EMPTY actionable row (Article summary, then How-to
  // steps). Any other empty actionable row is a quiet text action.
  const slotOwner: ActionableType | null =
    byType.get('Article')?.state === 'empty'
      ? 'Article'
      : byType.get('HowTo')?.state === 'empty'
        ? 'HowTo'
        : null;

  // Promotion cue (change blindness): keyed on the previous slot snapshot.
  // When the active slot fills and the next empty row promotes to the dashed
  // slot, the new slot renders with data-promoted + a one-time, non-color,
  // motion-safe emphasis (dashed border snapping in + brief ring pulse ≤1s —
  // CSS animation only, no timers). Never fires on mount or on the rows'
  // first arrival (hadRows guard).
  const [slotSnapshot, setSlotSnapshot] = useState<{
    owner: ActionableType | null;
    hadRows: boolean;
    promoted: ActionableType | null;
  }>({ owner: slotOwner, hadRows: hasRows, promoted: null });
  if (slotSnapshot.owner !== slotOwner || slotSnapshot.hadRows !== hasRows) {
    setSlotSnapshot({
      owner: slotOwner,
      hadRows: hasRows,
      promoted:
        slotSnapshot.hadRows && slotSnapshot.owner !== slotOwner ? slotOwner : null,
    });
  }
  const promotedType =
    slotSnapshot.owner === slotOwner && slotSnapshot.hadRows === hasRows
      ? slotSnapshot.promoted
      : null;

  // Restrained end-peak: true only when NOTHING awaits action — both
  // actionable rows hold real values.
  const bothFilled =
    byType.get('Article')?.state === 'value' &&
    byType.get('HowTo')?.state === 'value';

  /** Label line — 11px regular muted, hugging ITS value (asymmetric spacing).
      span.block (not <p>) so it can nest inside the row <button>. */
  const labelLine = (type: SchemaFocusType) => (
    <span className="block mt-0.5 text-[11px] text-muted-foreground leading-snug">
      {TYPE_LABELS[type].label}{' '}
      <span className="text-[10px] text-muted-foreground">{TYPE_LABELS[type].tag}</span>
    </span>
  );

  /** Quiet trailing microtag for valued automatic rows — never loud. */
  const automaticTag = (
    <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      Automatic
    </span>
  );

  /** Ships-automatically rows: inert/flat, muted derived values. */
  const automaticRow = (type: 'BreadcrumbList' | 'FAQPage') => {
    const row = byType.get(type);
    if (!row) return null;
    return (
      <div key={type} data-testid={`presence-row-${type}`}>
        {row.state === 'value' ? (
          <p
            className={cn(
              'text-[13px] font-medium text-muted-foreground leading-snug',
              // One-line truncation stays ONLY on the derived Breadcrumb trail.
              type === 'BreadcrumbList' && 'truncate',
            )}
          >
            {row.detail}
            {automaticTag}
          </p>
        ) : row.state === 'insufficient' ? (
          // Won't-ship honesty (publish gates FAQPage at ≥2 pairs): a single
          // question is NOT a shipping value. The row stays inert and
          // informational — still an automatic row, honestly not shipping.
          <p className="text-[11px] italic text-muted-foreground leading-snug">
            {row.detail}
          </p>
        ) : row.state === 'empty' && type === 'FAQPage' ? (
          // FAQ empty = quiet inert text, NEVER a dashed slot — there is
          // nothing to do on this row (FAQ comes from body blocks).
          <p className="text-[11px] italic text-muted-foreground leading-snug">
            No FAQ blocks in the article
          </p>
        ) : (
          <p className="text-[13px] italic text-muted-foreground leading-snug truncate">
            {row.detail}
          </p>
        )}
        {labelLine(type)}
      </div>
    );
  };

  /** Needs-your-text rows: every state is a button that opens the drawer focused. */
  const actionableRow = (type: ActionableType) => {
    const row = byType.get(type);
    if (!row) return null;
    const ariaLabel = `Open ${TYPE_LABELS[type].label} details`;
    // While the authoring run is in flight the two actionable rows show a
    // LABELED async skeleton (the wait must be named, never a bare
    // spinner). Automatic rows are untouched.
    if (authoring) {
      return (
        <div key={type} data-testid={`presence-row-${type}`}>
          <div
            data-testid={`authoring-skeleton-${type}`}
            className="flex items-center gap-2 rounded-md border border-dashed border-border bg-warm-bg/60 px-2.5 py-2"
          >
            <span
              aria-hidden="true"
              className="h-2.5 w-16 rounded-full bg-muted-foreground/25 motion-safe:animate-pulse"
            />
            <span className="text-[11px] italic text-muted-foreground">
              {SKELETON_LABELS[type]}
            </span>
          </div>
          {labelLine(type)}
        </div>
      );
    }
    return (
      <div key={type} data-testid={`presence-row-${type}`}>
        {row.state === 'value' ? (
          <button
            type="button"
            onClick={() => onInspect(type)}
            aria-label={ariaLabel}
            className={cn(
              'w-full text-left rounded-md cursor-pointer',
              FOCUS_RING,
            )}
          >
            <span
              className={cn(
                'block text-[13px] font-medium text-foreground leading-snug',
                type === 'Article' && 'line-clamp-2',
              )}
            >
              {row.detail}
            </span>
            {labelLine(type)}
          </button>
        ) : slotOwner === type ? (
          <>
            <button
              type="button"
              onClick={() => onInspect(type)}
              aria-label={ariaLabel}
              data-testid="imperative-slot"
              data-promoted={promotedType === type ? 'true' : undefined}
              className={cn(
                // D2: the single dashed slot owns primary next-step emphasis
                // — teal-dark is the one "look here" accent.
                'w-full flex items-center rounded-md border border-dashed border-teal-dark bg-warm-bg/60 px-2.5 py-2 text-left cursor-pointer',
                FOCUS_RING,
                promotedType === type && 'animate-slot-promote',
              )}
            >
              <span className="text-[13px] font-medium text-teal-dark">
                {SLOT_IMPERATIVES[type]}
              </span>
            </button>
            {labelLine(type)}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => onInspect(type)}
              aria-label={ariaLabel}
              data-testid={`quiet-action-${type}`}
              className={cn(
                'text-[11px] text-muted-foreground underline underline-offset-2 hover:text-foreground cursor-pointer rounded-sm',
                FOCUS_RING,
              )}
            >
              {SLOT_IMPERATIVES[type]}
            </button>
            {labelLine(type)}
          </>
        )}
      </div>
    );
  };

  /** Product is ALWAYS inert-muted — no backend authored slot exists. It
      lives BELOW the group (D2 emphasis discipline): a recessive single
      line that can no longer read as actionable. */
  const productRow = (
    <p
      key="Product"
      data-testid="presence-row-Product"
      className="mt-2 text-[11px] italic text-muted-foreground"
    >
      <span>Product details</span> — <span>Not available yet</span>{' '}
      <span className="text-[10px] text-muted-foreground">Product</span>
    </p>
  );

  const enrichmentBlock = (
    <div className="flex items-center gap-2 pt-1">
      {/* D2: teal owns "look here"; the LLM chip is quieted to neutral —
          no coral on this surface. */}
      <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-card text-muted-foreground border border-border">
        <Sparkles className="size-2.5" />
        LLM
      </span>
      <span className="text-[10.5px] text-muted-foreground">
        Enrichment blob tracked · not shipped
      </span>
    </div>
  );

  return (
    <Card railSection="structured-data">
      {/* The title must not collide with the SERP preview card —
          disambiguation lives in the TITLE ("Google rich results" fails the
          "is this where I edit the snippet?" test on purpose), never in a
          helper line. The sub-line must scope the claim to what THIS
          platform adds at publish: two graphs ship on bridge tenants and
          the SEO plugin's head graph is not ours to claim. */}
      <CardHeader
        icon={<Braces className="size-3.5" />}
        title="Google rich results"
        trailing={
          <span className="text-[11px] text-muted-foreground">Schema</span>
        }
      />

      <div className="px-3 pb-4">
        <p className="text-[11px] text-muted-foreground leading-snug mb-1.5">
          Added by this platform at publish — breadcrumbs, FAQ, how-to &amp; more
        </p>
        {hasRows ? (
          <div className="space-y-1.5">
            {/* Two symmetric chunks; clearly-defined areas via spacing only
                (12px row-gap inside groups, 20px between groups — no
                boxes-in-boxes). Group headers: 11px uppercase muted. */}
            <div className="space-y-5">
              <div>
                <p
                  data-testid="group-header-ships-automatically"
                  className="text-[11px] uppercase tracking-wide text-muted-foreground"
                >
                  Ships automatically
                </p>
                <div className="mt-1.5 space-y-3">
                  {automaticRow('BreadcrumbList')}
                  {automaticRow('FAQPage')}
                </div>
              </div>
              <div>
                <p
                  data-testid="group-header-needs-your-text"
                  className="text-[11px] uppercase tracking-wide text-muted-foreground"
                >
                  Needs your text
                </p>
                <div className="mt-1.5 space-y-3">
                  {actionableRow('Article')}
                  {actionableRow('HowTo')}
                </div>
              </div>
            </div>

            {/* Product sits BELOW the group — inert/recessive. */}
            {productRow}

            {enrichmentPresent && enrichmentBlock}
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-[11.5px] text-muted-foreground leading-snug">
              Inspect the composed JSON-LD graph that would ship at publish —
              per node, per field, with source tags.
            </p>
            {enrichmentPresent && enrichmentBlock}
          </div>
        )}

        {/* Restrained teal end-peak (graft): exactly ONE quiet line when
            nothing awaits action — no badge, no celebration. */}
        {bothFilled && !authoring && (
          <p data-testid="ready-line" className="mt-2.5 text-[13px] font-medium text-teal-dark">
            Ready — ships at publish
          </p>
        )}

        <button
          type="button"
          onClick={() => onInspect()}
          className={cn(
            'mt-2.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md',
            'text-[11.5px] font-medium text-teal-dark bg-teal-light border border-teal/30',
            'hover:bg-teal/10 cursor-pointer transition-colors',
          )}
        >
          <SquarePen className="size-3.5" />
          Edit details
        </button>
      </div>
    </Card>
  );
}
